import { customsRequestStrings } from '@data/customsRequest';
import { AdminInquiryDetail, AdminInquiryDetailHelper } from '@models/BookingInquiry';
import { CustomsRequestStatus } from '@models/next/CustomsRequestStatus';
import { Partner } from '@models/next/Partner';
import { secureDataOf } from '@models/next/SecureData';
import { AdminUser } from '@models/next/User';
import { BaseUser } from '@models/User';
import { makeMessagesEitherRightIf, MessagesEither } from '@utils/errors';
import { f } from '@utils/nextForms';
import * as R from 'remeda';
import { z } from 'zod';

export const CustomsRequestURLSubmitResult = z.object({
  url: z.string(),
});

export type CustomsRequestURLSubmitResult = z.infer<typeof CustomsRequestURLSubmitResult>;

export const CustomsRequestOwnerCompany = z.object({
  importer_code: z.string().label(customsRequestStrings.importer_code),
  real_time_account: z.string().label(customsRequestStrings.real_time_account),
  deferred_payment_account: z.string().label(customsRequestStrings.deferred_payment_account),
});

export type CustomsRequestOwnerCompany = z.infer<typeof CustomsRequestOwnerCompany>;

export const CustomsRequestSubmissionTargetType = z.enum(['EXTERNAL', 'INTERNAL']);

export const CustomsRequestAction = z.enum(['', 'SUBMIT', 'COMPLETE']);

export type CustomsRequestAction = z.infer<typeof CustomsRequestAction>;

export const CustomsRequestSubmissionTarget = z.object({
  emails: z.string().array(),
  company: z.string(),
  type: CustomsRequestSubmissionTargetType,
  telephone: z.string(),
  id: z.number().nullish(),
});

export type CustomsRequestSubmissionTarget = z.infer<typeof CustomsRequestSubmissionTarget>;

export const CustomsRequest = z.object({
  d2d_no: z.string().label(customsRequestStrings.d2d_no),
  cargo_move_in_date: f.dateString().label(customsRequestStrings.cargo_move_in_date),
  free_time: f.dateString().label(customsRequestStrings.free_time),
  tentative_pickup_date: f.dateString().label(customsRequestStrings.tentative_pickup_date),
  container_no: z.string().label(customsRequestStrings.container_no),
  memo: z.string().label(customsRequestStrings.memo),
  submission_target: CustomsRequestSubmissionTarget.nullish().label(
    customsRequestStrings.submissionTarget,
  ),
  status: CustomsRequestStatus.array().label(customsRequestStrings.status),
  creator: AdminUser.label(customsRequestStrings.creator),
  check_type: z.string().nullish().label(customsRequestStrings.check_type),
  created_at: z.string(),
  updated_at: z.string(),

  owner_company: CustomsRequestOwnerCompany,
  special_note: z.string().label(customsRequestStrings.special_note),
});

export type CustomsRequest = z.infer<typeof CustomsRequest>;

export const CustomsRequestSecureData = secureDataOf(CustomsRequest);

export type CustomsRequestSecureData = z.infer<typeof CustomsRequestSecureData>;

export class CustomsRequestHelper {
  constructor(public r: CustomsRequest) {}

  getSubmittedAt() {
    return this.getCurrentSubmissionStatus()[0]?.time;
  }

  toForm(): CustomsRequestForm {
    return {
      ...this.r,
      submission_target_company: this.r.submission_target?.company ?? '',
      action: 'save',
    };
  }

  isSelfSubmissionTarget() {
    return this.r.submission_target?.type === 'INTERNAL';
  }

  canAccept(user: BaseUser, bookingInquiry: AdminInquiryDetail) {
    return new AdminInquiryDetailHelper(bookingInquiry).canEditAsSalespersonCustoms(user);
  }

  /**
   * Get the status list after the last 通関依頼書送信済. This is the valid status list for the current submission
   */
  getCurrentSubmissionStatus(): CustomsRequestStatus[] {
    const submittedIndex = R.findLastIndex(this.r.status, (s) => s.name === '通関依頼書送信済');

    if (submittedIndex === -1) return [];

    return this.r.status.slice(submittedIndex);
  }

  canSubmit(): MessagesEither {
    return R.pipe(this.r.status.at(-1)?.name, (s) =>
      makeMessagesEitherRightIf(
        s === '通関依頼書作成済' ||
          s === '通関依頼書送信済' ||
          s === '通関リンク作成済' ||
          s === '通関準備中',
        {
          messages: [customsRequestStrings.errorCannotSubmit(s!)],
        },
      ),
    );
  }

  getSubmissionCount(): number {
    return this.r.status.filter((s) => s.name === '通関依頼書送信済').length;
  }

  static mapPartnerToSubmissionTarget(p: Partner): CustomsRequestSubmissionTarget {
    return {
      emails: p.contacts.map((c) => c.email),
      company: p.name_en_short,
      telephone: p.tel,
      type: 'EXTERNAL',
      id: p.id,
    };
  }
}

export const PostCustomsRequest = z.object({
  d2d_no: CustomsRequest.shape.d2d_no,
  cargo_move_in_date: CustomsRequest.shape.cargo_move_in_date,
  free_time: CustomsRequest.shape.free_time,
  tentative_pickup_date: CustomsRequest.shape.tentative_pickup_date,
  container_no: CustomsRequest.shape.container_no,
  memo: CustomsRequest.shape.memo,
  owner_company: CustomsRequest.shape.owner_company,
  special_note: CustomsRequest.shape.special_note,
});

export type PostCustomsRequest = z.infer<typeof PostCustomsRequest>;

export const PatchCustomsRequest = z.object({
  cargo_move_in_date: CustomsRequest.shape.cargo_move_in_date,
  free_time: CustomsRequest.shape.free_time,
  tentative_pickup_date: CustomsRequest.shape.tentative_pickup_date,
  container_no: CustomsRequest.shape.container_no,
  memo: CustomsRequest.shape.memo,
  owner_company: CustomsRequest.shape.owner_company,
  special_note: CustomsRequest.shape.special_note,
});

export type PatchCustomsRequest = z.infer<typeof PatchCustomsRequest>;

export const CustomsRequestForm = z.object({
  cargo_move_in_date: CustomsRequest.shape.cargo_move_in_date.min(1),
  free_time: CustomsRequest.shape.free_time.min(1),
  tentative_pickup_date: CustomsRequest.shape.tentative_pickup_date.min(1),
  container_no: CustomsRequest.shape.container_no,
  memo: CustomsRequest.shape.memo,
  submission_target_company: z.string().label(customsRequestStrings.submissionTarget),

  owner_company: CustomsRequest.shape.owner_company,
  special_note: CustomsRequest.shape.special_note,

  action: z.enum(['save', 'submit', 'urlSubmit']),
});

export type CustomsRequestForm = z.infer<typeof CustomsRequestForm>;

export const CustomsRequestSubmitForm = z.object({
  company: z.string().label(customsRequestStrings.submissionTarget).min(1),
});

export type CustomsRequestSubmitForm = z.infer<typeof CustomsRequestSubmitForm>;

export const PutCustomsRequestBody = z
  .object({
    submission_target: CustomsRequestSubmissionTarget,
  })
  .partial();

export type PutCustomsRequestBody = z.infer<typeof PutCustomsRequestBody>;
